import { useEffect } from "preact/hooks";
import { AccountMenu, AppLayout, Button, EmptyState, Field, Notice, PageHeader, Select, SampleMark, Switch, useTheme } from "../../../ui/src/index.ts";
import type { AppLink, NavigationEntry, NavigationItem } from "../../../ui/src/index.ts";
import type { HubId } from "../../../src/exchange/curator-api.ts";
import { isLive } from "../domain/activity.ts";
import { NEEDS_YOU_REFRESH_MS, needsYouCount, needsYouDot, waitingInteractions } from "../domain/agent.ts";
import { pricingRows } from "../domain/pricing.ts";
import type { HubSummary, HubRecord } from "../domain/types.ts";
import { loadAgent, loadHubs, loadWaitingInteractions, signIn, signOut } from "./actions.ts";
import { setToursOn } from "./guide-state.ts";
import { hubLabel } from "./hubData.ts";
import { GuidedTour, HelpDrawer } from "./Guide.tsx";
import { Dashboard, DashboardPlaceholder } from "./pages/Dashboard.tsx";
import { AgentActivity } from "./pages/Agent.tsx";
import { Demand } from "./pages/Demand.tsx";
import { Golden } from "./pages/Golden.tsx";
import { Help } from "./pages/Help.tsx";
import { Overview } from "./pages/Overview.tsx";
import { NeedsYou } from "./pages/NeedsYou.tsx";
import { NewHubs, Register } from "./pages/Register.tsx";
import { Pricing } from "./pages/Pricing.tsx";
import { Question, Questions } from "./pages/Questions.tsx";
import { Reputation } from "./pages/Reputation.tsx";
import { Toolsets } from "./pages/Toolsets.tsx";
import { Failed, Loading } from "./parts.tsx";
import { href, navigate, routedHub, useRoute, type ParsedRoute } from "./router.ts";
import { useStore } from "./store.ts";

// The hub agent's page, on every hub's navigation: a hub without a toolset has a hub agent too. Its count is the Interactions that wait on the curator, once read.
const agentEntry = (route: ParsedRoute, id: HubId, waiting: number | undefined): NavigationEntry =>
  ({ label: "Agent activity", icon: "user", ...(waiting ? { count: waiting } : {}), href: href({ name: "agent", hub: id }), current: route.name === "agent" });

function navigation(route: ParsedRoute, hub: HubSummary, declined: number | undefined, pricedOut: number | undefined, waiting: number | undefined): NavigationEntry[] {
  const id = hub.hub.id;
  const page = route.name;
  return [
    { label: "Hub overview", icon: "overview", href: href({ name: "overview", hub: id }), current: page === "overview" },
    { label: "Questions", icon: "questions", count: hub.manifest.intents.filter(isLive).length, href: href({ name: "questions", hub: id }), current: page === "questions" || page === "question" },
    { label: "Toolsets", icon: "toolsets", href: href({ name: "toolsets", hub: id }), current: page === "toolsets" },
    { label: "Golden questions", icon: "test", href: href({ name: "golden", hub: id }), current: page === "golden" },
    agentEntry(route, id, waiting),
    { heading: "Grow" },
    { label: "Pricing signals", icon: "pricing", ...(pricedOut !== undefined ? { count: pricedOut } : {}), href: href({ name: "pricing", hub: id }), current: page === "pricing" },
    { label: "Demand", icon: "demand", ...(declined !== undefined ? { count: declined } : {}), href: href({ name: "demand", hub: id, group: "declined", days: 30, search: "" }), current: page === "demand" },
    { heading: "Trust" },
    { label: "Reputation", icon: "reputation", href: href({ name: "reputation", hub: id }), current: page === "reputation" },
  ];
}

/**
 * The navigation's top level, above the hub picker: All hubs, and Needs you
 * with the count of Interactions waiting on the curator across every hub, once read and
 * when there are any, and a dot while an inform is unread, which never
 * counts. See Design > UI, "Needs you".
 */
export const topLevel = (route: ParsedRoute, needsYou: number | undefined, unread = false): NavigationItem[] => [
  { label: "All hubs", icon: "hubs", href: href({ name: "home" }), current: route.name === "home" },
  { label: "Needs you", icon: "user", ...(needsYou ? { count: needsYou } : {}), ...(unread ? { dot: "something new to read" } : {}), href: href({ name: "needs-you" }), current: route.name === "needs-you" },
];

// A hub with no toolset yet has two pages: the Toolsets, where it gets its first, and its agent activity.
const pendingNavigation = (route: ParsedRoute, record: HubRecord, waiting: number | undefined): NavigationEntry[] =>
  [{ label: "Toolsets", icon: "toolsets", href: href({ name: "toolsets", hub: record.id }), current: route.name === "toolsets" }, agentEntry(route, record.id, waiting)];

// The wordmark's app switcher: every curator can use the Console too.
const apps = (consoleUrl: string): AppLink[] => [
  { name: "Console", icon: "questions", href: consoleUrl, description: "Ask questions and see what answers cost" },
  { name: "Curator Studio", icon: "hub", href: "/", description: "For curators: your hubs' earnings, questions and demand", current: true },
];

export function App() {
  const route = useRoute();
  const hubs = useStore((s) => s.hubs);
  const demand = useStore((s) => s.demand);
  const account = useStore((s) => s.sample.account);
  const user = useStore((s) => s.user);
  // The Curator API needs a bearer token, so nothing is read until a curator
  // signs in. Where the environment configures no AuthKit, no one can.
  const signInConfigured = useStore((s) => s.config.authkit !== null);
  const consoleUrl = useStore((s) => s.config.consoleUrl);
  const [theme, setTheme] = useTheme();
  const toursOn = useStore((s) => s.toursOn);
  useEffect(() => { if (user) void loadHubs(); }, [user]);
  // Needs you: read on sign-in, then once a minute while the page is in
  // view, and as soon as it comes back into view. Sign-out stops the timer.
  // See Design > UI, "Needs you".
  useEffect(() => {
    if (!user) return;
    void loadWaitingInteractions();
    const tick = () => { if (!document.hidden) void loadWaitingInteractions(); };
    const timer = setInterval(tick, NEEDS_YOU_REFRESH_MS);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", tick); };
  }, [user]);
  const waitingLoad = useStore((s) => s.waitingInteractions);
  const needsYou = needsYouCount(waitingLoad?.status === "ok" ? waitingLoad.value.interactions : undefined);
  const unread = needsYouDot(waitingLoad?.status === "ok" ? waitingLoad.value.unread : undefined);

  const list = hubs?.status === "ok" ? hubs.value.hubs : [];
  const pending = hubs?.status === "ok" ? hubs.value.hubsWithoutToolset : [];
  const { hub, record: selectedRecord } = routedHub(route, list, pending);
  const pendingHub = hub ? undefined : selectedRecord;
  const records = [...list.map((h) => h.hub), ...pending];

  // Home is the dashboard: every hub at a glance. "All hubs" sits above the picker, as the sidebar's home link, so it keeps its icon when the sidebar is collapsed.
  const home = route.name === "home";
  const picker = list.length + pending.length > 0 ? <>
    <Field id="studio-hub" label="Selected hub">{(attrs) => (
      <Select {...attrs} value={selectedRecord?.id ?? ""} title={selectedRecord?.name} onChange={(e) => {
        const picked = routedHub({ name: "overview", hub: e.currentTarget.value }, list, pending).record;
        if (picked) navigate({ name: "overview", hub: picked.id });
      }}>
        {!selectedRecord && <option value="">Choose a hub</option>}
        {records.map((r) => <option key={r.id} value={r.id}>{hubLabel(r.name, hubs)}</option>)}
      </Select>)}</Field>
  </> : undefined;
  const declinedLoad = hub ? demand[`${hub.hub.id}:30`] : undefined;
  const declined = declinedLoad?.status === "ok" ? declinedLoad.value.declined.questions : undefined;
  const hubEvents = useStore((st) => (hub ? st.events[hub.hub.id] : undefined));
  const pricedOut = hub && hubEvents?.status === "ok" ? pricingRows(hub, hubEvents.value.events).filter((r) => r.signal === "pricedOut").length : undefined;
  // Read once a hub is selected, so the navigation counts the Interactions waiting from any page.
  useEffect(() => { if (selectedRecord) void loadAgent(selectedRecord.id); }, [selectedRecord?.id]);
  const agentLoad = useStore((st) => (selectedRecord ? st.agent[selectedRecord.id] : undefined));
  const waiting = agentLoad?.status === "ok" ? waitingInteractions(agentLoad.value.interactions).length : undefined;

  const studioHeader = <PageHeader title={<>Curator <strong>Studio</strong></>} description="How your hub earns, the questions it answers, and the asks it misses." />;
  let page;
  if (!signInConfigured) page = <>{studioHeader}<Notice title="Sign-in isn't configured here yet">
    Curator Studio shows the hubs you curate, so it needs you to sign in with WarmHub, and this environment has no sign-in yet.
  </Notice></>;
  else if (!user) page = <>{studioHeader}<Notice title="Sign in to see your hubs" action={<Button size="sm" onClick={signIn}>Sign in with WarmHub</Button>}>
    Curator Studio shows the hubs you curate. Sign in with your WarmHub account.
  </Notice></>;
  else if (!hubs || hubs.status === "loading") page = home ? <DashboardPlaceholder loading /> : <>{studioHeader}<Loading label="Loading hubs" /></>;
  else if (hubs.status === "failed") page = <>{studioHeader}<Failed what="the hubs" error={hubs.error} retry={() => void loadHubs(true)} /></>;
  else if (route.name === "register") page = <Register />;
  else if (route.name === "needs-you") page = <NeedsYou />;
  else if (home && list.length > 0) page = <Dashboard hubs={list} />;
  else if (home && pending.length > 0) page = <>{studioHeader}<NewHubs records={pending} /></>;
  else if (home) page = <>{studioHeader}<EmptyState>You don't curate any hubs on this Exchange yet.<br /><Button size="sm" variant="solid" href={href({ name: "register" })}>Register a hub</Button></EmptyState></>;
  else if (!hub && pendingHub && route.name === "toolsets") page = <Toolsets key={pendingHub.id} record={pendingHub} />;
  else if (!hub && pendingHub && route.name === "agent") page = <AgentActivity record={pendingHub} />;
  else if (!hub && pendingHub) page = <>{studioHeader}<EmptyState>{hubLabel(pendingHub.name, hubs)} has no toolset yet, so it has no questions, earnings or demand to show.<br /><Button size="sm" variant="solid" href={href({ name: "toolsets", hub: pendingHub.id })}>Set up its first toolset</Button></EmptyState></>;
  else if (!hub) page = <>{studioHeader}<EmptyState>There's no such page or hub here. Choose a hub.</EmptyState></>;
  else if (route.name === "toolsets") page = <Toolsets key={hub.hub.id} record={hub.hub} />;
  else if (route.name === "overview") page = <Overview hub={hub} tab={route.tab ?? "summary"} />;
  else if (route.name === "questions") page = <Questions hub={hub} />;
  else if (route.name === "question") page = <Question hub={hub} intentId={route.intent} />;
  else if (route.name === "golden") page = <Golden hub={hub} />;
  else if (route.name === "agent") page = <AgentActivity record={hub.hub} hub={hub} />;
  else if (route.name === "demand") page = <Demand hub={hub} route={{ ...route, hub: hub.hub.id }} />;
  else if (route.name === "pricing") page = <Pricing hub={hub} />;
  else if (route.name === "reputation") page = <Reputation hub={hub} />;
  else if (route.name === "help") page = <Help hub={hub} route={{ ...route, hub: hub.hub.id }} />;

  // All hubs, the Hub overview, a Question page and Agent activity draw their own bar and workspace edge to edge. See Design > UI, "Workspace".
  const flush = (home && signInConfigured && Boolean(user) && hubs?.status !== "failed") || (Boolean(hub) && (route.name === "overview" || route.name === "question")) || (Boolean(selectedRecord) && route.name === "agent");
  return <AppLayout product="Curator Studio" density="compact" flush={flush} contextIcon="hub" {...(list.length + pending.length > 0 ? { home: topLevel(route, needsYou, unread) } : {})} context={picker} apps={apps(consoleUrl)}
    navigation={hub ? navigation(route, hub, declined, pricedOut, waiting) : pendingHub ? pendingNavigation(route, pendingHub, waiting) : []}
    foot={[{ label: "Register a hub", icon: "plus", href: href({ name: "register" }), current: route.name === "register" },
      ...(hub ? [{ label: "Help", icon: "help" as const, href: href({ name: "help", hub: hub.hub.id, item: "" }), current: route.name === "help" }] : [])]}
    account={<AccountMenu {...(user ? { name: user.email, email: user.email } : { name: account.name, email: account.email, org: account.org, mark: <SampleMark title="A sample account; you aren't signed in" /> })}
      theme={theme} onThemeChange={setTheme}
      settings={<Switch label="Guided walkthroughs" hint="Offer one on pages you haven't toured" checked={toursOn} onChange={setToursOn} />}
      actions={[{ label: "Settings", icon: "settings", unavailable: "Coming soon" }, user ? { label: "Sign out", icon: "signOut", onSelect: signOut } : { label: "Sign out", icon: "signOut", unavailable: "Not signed in" }]} />}>
    {page}
    {hub && <HelpDrawer route={route} hub={hub} />}
    {hub && <GuidedTour route={route} />}
  </AppLayout>;
}
